import React, { useEffect } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import {
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	Box,
	Grid,
	Typography,
} from "@mui/material";
import { useSelector } from "react-redux";

import DropdownController from "../../components/inputs/DropdownController";
import TextFieldController from "../../components/inputs/TextFieldController";
import PATCloseButton from "../../components/Buttons/PATCloseButton";
import PATSaveButton from "../../components/Buttons/PATSaveButton";
import { maxLengthMessage } from "../../utils/functions";

// ── Validation Schema ───────────────────────────────────────────────────────
const checkoutSchema = yup.object().shape({
	status: yup.string().trim().required("Status is required"),
	assignedOfficerID: yup
		.string()
		.nullable()
		.transform((v, o) => (o === "" ? null : v)),
	assignedVehicleID: yup
		.string()
		.nullable()
		.transform((v, o) => (o === "" ? null : v)),
	location: yup.string().trim().max(100, maxLengthMessage(100)).nullable(),
	notes: yup.string().trim().max(500, maxLengthMessage(500)).nullable(),
});

// ── Default Values Builder ──────────────────────────────────────────────────
const buildDefaults = (eq) => ({
	status: eq?.status || "",
	assignedOfficerID: eq?.assignedOfficerID
		? String(eq.assignedOfficerID)
		: "",
	assignedVehicleID: eq?.assignedVehicleID
		? String(eq.assignedVehicleID)
		: "",
	location: eq?.location || "",
	notes: "",
});

// ── Component ───────────────────────────────────────────────────────────────
const EquipmentCheckInOutDialog = ({
	open,
	onClose,
	onSave,
	initialData,
	statusOptions = [],
	employeeOptions = [],
	vehicleOptions = [],
}) => {
	const user = useSelector((state) => state.user);

	const {
		control,
		handleSubmit,
		reset,
		watch,
		formState: { errors },
	} = useForm({
		resolver: yupResolver(checkoutSchema),
		defaultValues: buildDefaults(initialData),
	});

	// Reset form when dialog opens with new data
	useEffect(() => {
		reset(buildDefaults(initialData));
	}, [initialData, reset]);

	// Clear form on close
	useEffect(() => {
		if (!open) reset();
	}, [open, reset]);

	const onSubmit = (data) => {
		const payload = {
			equipmentId: initialData?.id,
			status: data.status || null,
			assignedOfficerID: data.assignedOfficerID || null,
			assignedVehicleID: data.assignedVehicleID || null,
			location: data.location?.trim() || null,
			notes: data.notes?.trim() || null,
			agencyId: user?.userAgency || null,
			checkedOutAt: new Date().toISOString(),
		};

		onSave(payload);
		onClose();
	};

	if (!initialData) return null;

	const eq = initialData;
	const curStatusDef = statusOptions.find((s) => s.value === eq.status);
	const isOut = curStatusDef?.isCheckedOut === true;

	// Filter statuses to show opposite state (check-in shows check-in statuses, etc.)
	const relevantStatuses = statusOptions.filter(
		(s) => s.isCheckedOut === !isOut,
	);
	const displayStatuses =
		relevantStatuses.length > 0 ? relevantStatuses : statusOptions;

	// Find current assignments for summary display
	const curEmp = employeeOptions.find(
		(e) => String(e.value) === String(eq.assignedOfficerID),
	);
	const curVeh = vehicleOptions.find(
		(v) => String(v.value) === String(eq.assignedVehicleID),
	);

	const dialogTitle = isOut
		? `Check In — ${eq.type} ${eq.serialNumber || ""}`
		: `Check Out — ${eq.type} ${eq.serialNumber || ""}`;

	const selectedEmpId = watch("assignedOfficerID");
	const selectedEmp = employeeOptions.find(
		(e) => String(e.value) === String(selectedEmpId),
	);

	return (
		<Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
			<DialogTitle>{dialogTitle}</DialogTitle>

			<DialogContent>
				<Box
					component="form"
					onSubmit={handleSubmit(onSubmit)}
					noValidate
					sx={{ mt: 1 }}
				>
					{/* ── Item Summary ── */}
					<Box
						sx={{
							display: "flex",
							gap: 2,
							p: 2,
							bgcolor: "background.paper",
							border: "1px solid",
							borderColor: "divider",
							borderRadius: 1,
							alignItems: "center",
							mb: 2,
						}}
					>
						<Typography sx={{ fontSize: 24 }}>🔫</Typography>
						<Box sx={{ flex: 1 }}>
							<Typography fontWeight={700} fontSize={14}>
								{eq.type}
								{eq.brand ? ` — ${eq.brand}` : ""}
								{eq.model ? ` ${eq.model}` : ""}
							</Typography>
							<Typography
								variant="caption"
								color="text.secondary"
								fontFamily="monospace"
								sx={{ mt: 0.5, display: "block" }}
							>
								{eq.serialNumber ||
									eq.propertyNumber ||
									eq.assetTag ||
									`#${eq.id}`}
							</Typography>
						</Box>
						<Box sx={{ textAlign: "right" }}>
							<Typography
								variant="caption"
								fontWeight={700}
								color={isOut ? "warning.main" : "success.main"}
							>
								{eq.status}
							</Typography>
							{eq.location && (
								<Typography
									variant="caption"
									color="text.secondary"
									sx={{ mt: 0.5, display: "block" }}
								>
									{eq.location}
								</Typography>
							)}
						</Box>
					</Box>

					{/* ── Current Assignment Summary ── */}
					<Grid container spacing={2} sx={{ mb: 2 }}>
						<Grid item xs={6}>
							<Box
								sx={{
									p: 1.5,
									bgcolor: "background.default",
									border: "1px solid",
									borderColor: "divider",
									borderRadius: 1,
								}}
							>
								<Typography
									variant="caption"
									fontWeight={700}
									color="text.secondary"
									sx={{
										textTransform: "uppercase",
										letterSpacing: "0.06em",
										display: "block",
										mb: 0.5,
									}}
								>
									Currently Assigned To
								</Typography>
								{curEmp ? (
									<Typography
										variant="body2"
										fontWeight={600}
									>
										{curEmp.label}
									</Typography>
								) : (
									<Typography
										variant="body2"
										color="text.secondary"
										fontStyle="italic"
									>
										Unassigned
									</Typography>
								)}
							</Box>
						</Grid>
						<Grid item xs={6}>
							<Box
								sx={{
									p: 1.5,
									bgcolor: "background.default",
									border: "1px solid",
									borderColor: "divider",
									borderRadius: 1,
								}}
							>
								<Typography
									variant="caption"
									fontWeight={700}
									color="text.secondary"
									sx={{
										textTransform: "uppercase",
										letterSpacing: "0.06em",
										display: "block",
										mb: 0.5,
									}}
								>
									Current Vehicle
								</Typography>
								{curVeh ? (
									<Typography
										variant="body2"
										fontWeight={600}
									>
										{curVeh.label}
									</Typography>
								) : (
									<Typography
										variant="body2"
										color="text.secondary"
										fontStyle="italic"
									>
										None
									</Typography>
								)}
							</Box>
						</Grid>
					</Grid>

					{/* ── Form Fields ── */}
					<Grid container spacing={2}>
						<Grid item xs={12}>
							<DropdownController
								name="status"
								label={`New Status ${displayStatuses.length === 0 ? "(no statuses defined)" : ""}`}
								options={displayStatuses.map((s) => ({
									value: s.value,
									label: s.value,
								}))}
								control={control}
								errors={errors}
							/>
						</Grid>

						<Grid item xs={12}>
							<DropdownController
								name="assignedOfficerID"
								label={
									isOut
										? "Return to / Assign To (optional)"
										: "Check Out To"
								}
								options={[
									{ value: "", label: "— Unassigned —" },
									...employeeOptions,
								]}
								control={control}
								errors={errors}
								placeholder="— Select employee —"
							/>
							{selectedEmp && (
								<Box
									sx={{
										mt: 1,
										p: 1,
										bgcolor: "primary.lighter",
										border: "1px solid",
										borderColor: "primary.light",
										borderRadius: 1,
									}}
								>
									<Typography
										variant="body2"
										fontWeight={600}
									>
										{selectedEmp.label}
									</Typography>
								</Box>
							)}
						</Grid>

						<Grid item xs={12}>
							<DropdownController
								name="assignedVehicleID"
								label="Vehicle (optional)"
								options={[
									{ value: "", label: "— No vehicle —" },
									...vehicleOptions,
								]}
								control={control}
								errors={errors}
								placeholder="— Select vehicle —"
							/>
						</Grid>

						<Grid item xs={12}>
							<TextFieldController
								name="location"
								label="Location (optional)"
								placeholder={
									isOut
										? "e.g. Armory, Storage Room 2..."
										: "e.g. Vehicle P-101, Belt..."
								}
								control={control}
								errors={errors}
							/>
						</Grid>

						<Grid item xs={12}>
							<TextFieldController
								name="notes"
								label="Notes (optional)"
								placeholder="Purpose, condition, shift..."
								control={control}
								errors={errors}
								multiline
								rows={2}
							/>
						</Grid>
					</Grid>
				</Box>
			</DialogContent>

			<DialogActions sx={{ px: 3, pb: 2 }}>
				<PATSaveButton
					onClick={handleSubmit(onSubmit)}
					tooltipText={
						isOut ? "Confirm Check In" : "Confirm Check Out"
					}
				/>
				<PATCloseButton onClick={onClose} tooltipText="Cancel" />
			</DialogActions>
		</Dialog>
	);
};

export default EquipmentCheckInOutDialog;
